import 'ol/ol.css';
import Map from 'ol/Map';
import Static from 'ol/source/ImageStatic';
import ImageLayer from 'ol/layer/Image';
import LayerGroup from 'ol/layer/Group';
import {transformExtent, transform} from 'ol/proj';
import LocalScene, {
  LonLatCoordinate,
  ProjectedCoordinate
} from '../projections';
import { AerialMap } from '.';
import {
  polygon as turfPolygon,
  intersect as turfIntersect
} from '@turf/turf';

class OrthoImageLayer extends ImageLayer {
  constructor(opts) {
    super(opts);
  }
}

export class OrthoMosaic {
  public id;
  public layer: ImageLayer;
  private corners = [];
  public aerialBounds = [];
  private scaleX: number;
  private scaleY: number;
  private imageBase64: string;
  private name: string;
  private splitData;
  private cropBase64 = [];
  private cropCorners = [];
  private imageSize: [Number, Number];
  private imageSizeOriginal: [Number, Number];
  private imagePath: string;
  private fromJSON: boolean;
  private extent = [];
  private orthosList: OrthoMosaicList;
  public loading = false;
  public loaded = false;

  constructor(orthosList: OrthoMosaicList, data) {
    this.orthosList = orthosList;
    this.id = data.id;
    this.name = data.name;
    this.corners = data.corners;
    this.imageBase64 = data.base64;
    this.imageSize = data.imageSize;
    this.splitData = data.splitData;
    this.imageSizeOriginal = data.imageSizeOriginal;
    this.fromJSON = data.fromJSON;
    this.imagePath = data.imagePath;
    this.scaleX = data.scaleX;
    this.scaleY = data.scaleY;

    this.calculateCroppedData();
    this.calculateExtent();
    this.calculateAerialPolygon();
    this.addMapLayer();
  }

  get opacity() {
    return this.orthosList.opacity;
  }

  get aerialMap() {
    return this.orthosList.aerialMap;
  }

  get map() {
    return this.orthosList.map;
  }

  get projection() {
    return LocalScene.dataProjectionName;
  }

  get proj4Projection() {
    return LocalScene.dataProjection;
  }

  get visible() {
    return this.layer.getVisible();
  }

  get path() {
    return this.imagePath;
  }

  get cornerCoordinates() {
    return this.corners;
  }

  get scale() {
    return [this.scaleX, this.scaleY];
  }

  get originalSize() {
    return this.imageSizeOriginal;
  }

  get orthoName() {
    return this.name;
  }

  get metadata() {
    return {
      x_scale: this.scaleX,
      y_scale: this.scaleY,
      x_offset: this.corners[0],
      y_offset: this.corners[3],
      x_skew: 0.0,
      y_skew: 0.0
    };
  }

  get cornerPixels() {
    return [
      [0, 0],
      [this.originalSize[0], 0],
      [this.originalSize[0], this.originalSize[1]],
      [0, this.originalSize[1]],
    ] as Array<[number, number]>;
  }

  zoomToExtent() {
    this.aerialMap.setMapExtent(this.extent);
  }

  calculateExtent() {
    const extent = transformExtent(this.corners,
      this.projection, 'EPSG:3857');
    this.extent = extent;
  }

  calculateAerialPolygon() {
    this.aerialBounds = imagePixelsToAerial(
      this.cornerPixels, this, this.proj4Projection);
  }

  calculateCroppedData() {
    if (this.splitData) {
      this.cropBase64 = [];
      this.cropCorners = [];

      this.splitData.forEach(data => {
        // Cropped base64 data
        let {index_start, index_end} = data;
        let base64 = this.imageBase64.slice(index_start, index_end);
        this.cropBase64.push(base64);

        // Cropped extent
        let {width_start, width_end, height_start, height_end} = data;
        let extentWidth = this.corners[2] - this.corners[0];
        let extentHeight = this.corners[3] - this.corners[1];

        let subtileCorners = [
          this.corners[0] + extentWidth * width_start,
          this.corners[3] - (extentHeight * height_end),
          this.corners[0] + extentWidth * width_end,
          this.corners[3] - (extentHeight * height_start),
        ];

        this.cropCorners.push(subtileCorners);
      });
    } else {
      // Single value. Convert to array
      this.cropBase64 = [this.imageBase64];
      this.cropCorners = [this.corners];
    }
  }

  intersectAerialPoints(aerialPoints) {
    return turfIntersect(
      turfPolygon([[...aerialPoints, aerialPoints[0]]]),
      turfPolygon([[...this.aerialBounds, this.aerialBounds[0]]])
    );
  }

  aerialToPixels(aerialPoints) {
    return aerialToImagePixels(
      aerialPoints, this, this.proj4Projection);
  }

  transform(coordinates) {
    let coordinatesTransformed = [];
    coordinates.forEach(coordinate =>{
      let coordTransformed = transform(coordinate, 'EPSG:3857', this.projection);
      coordinatesTransformed.push(coordTransformed);
    });
    return coordinatesTransformed;
  }

  reverseTransform(coordinates) {
    let coordinatesTransformed = [];
    coordinates.forEach(coordinate =>{
      let coordTransformed = transform(coordinate, this.projection, 'EPSG:3857');
      coordinatesTransformed.push(coordTransformed);
    });
    return coordinatesTransformed;
  }

  addMapLayer() {
    let allLayers = [];
    let numSubtiles = this.cropBase64.length;

    console.log(`Load ortho layer (${numSubtiles} subtiles)`);

    for (var i =0; i<numSubtiles; i++) {
      // Add layer immediately to map
      let source = new Static({
        url: this.cropBase64[i],
        imageExtent: this.cropCorners[i],
        projection: this.projection,
        interpolate: false,
        imageLoadFunction: (image, src) => {
          let element = image.getImage();
          let data = {ortho: this, element, src};
          this.orthosList.addImageChunk(data);
        }
      });

      let layer = new OrthoImageLayer({source});

      // Add layer to group
      allLayers.push(layer);
    }

    // Craete layer group that contains all subtiles
    this.layer = new LayerGroup({layers: allLayers});
    this.setOpacity();

    this.map.addLayer(this.layer);

    if (this.fromJSON) return;
    this.zoomToExtent();
  }

  setOpacity() {
    this.layer.setOpacity(this.opacity);
  }

  setVisibility(visible) {
    this.layer.setVisible(visible);
  }

  destroy() {
    if (!this.layer) {
      return;
    }

    this.map.removeLayer(this.layer);
    this.imageBase64 = null;
    this.layer = null;
  }
}

export class OrthoMosaicList {
  public orthos: OrthoMosaic[] = [];
  public opacity = 0.8;
  public aerialMap: AerialMap;
  public maxAsyncLoad = 2;
  public chunksLoading = 0;
  public chunksToLoad = [];
  private enabled = true;

  constructor(aerialMap: AerialMap) {
    this.aerialMap = aerialMap;
    this.loadAvailableData();
  }

  get map(): Map {
    return this.aerialMap.map;
  }

  destroy() {
    this.enabled = false;
  }

  addNewOrtho(data) {
    let cameraID = data.id;
    if (this.getByID(cameraID)) {
      // Ortho already exists
      return false;
    }

    let ortho = new OrthoMosaic(this, data);
    this.orthos.push(ortho);
  }

  deleteOrtho(orthoID) {
    let ortho = this.getByID(orthoID);
    if (!ortho) {
      return;
    }

    ortho.destroy();
    const index = this.orthos.indexOf(ortho);
    this.orthos.splice(index, 1);
  }

  getByID(orthoID) {
    return this.orthos.find(ortho => ortho.id === orthoID);
  }

  addImageChunk(loadable) {
    this.chunksToLoad.push(loadable);
  }

  toggleVisibility = (orthoID, visible) => {
    let ortho = this.getByID(orthoID);
    if (!ortho) {
      return;
    }

    ortho.setVisibility(visible);
  }

  setOpacity = (value) => {
    if ((value < 0.0) || (value > 1.0)) {
      return;
    }

    this.opacity = value;
    this.orthos.forEach(ortho => ortho.setOpacity());
  }

  loadAvailableData() {
    if (!this.enabled) return false;

    if (this.chunksLoading < this.maxAsyncLoad) {
      let toLoad = this.chunksToLoad.pop();
      if (toLoad) {
        let {ortho, element, src} = toLoad;

        if (ortho.layer) {
          if (ortho.visible) {
            this.chunksLoading += 1;
            element.onload = () => {
              this.chunksLoading -= 1;
            };
            element.src = src;
          } else {
            this.chunksToLoad.push(toLoad);
          }
        }
      }
    }

    setTimeout(() => {
      this.loadAvailableData();
    }, 50);
  }
}

export const imagePixelsToAerial = (points, image: OrthoMosaic, projection): Array<[number, number]> => {
  let newPoints = [];

  try {
    let metadata = image.metadata;
    newPoints = points.map(point => {
      let x = point[0]*metadata.x_scale + metadata.x_offset;
      let y = point[1]*metadata.y_scale + metadata.y_offset;
      let lonlat = new ProjectedCoordinate([x,y,0], projection).toLonLat();
      return transform([lonlat.x, lonlat.y], 'EPSG:4326', 'EPSG:3857');
    });
  } catch {
    // Do Nothing
  }

  return newPoints;
};

export const aerialToImagePixels = (points, image: OrthoMosaic, projection): Array<[number, number]> => {
  let newPoints = [];


  let metadata = image.metadata;
  newPoints = points.map(point => {
    let lonlat = transform([point[0], point[1], 0], 'EPSG:3857', 'EPSG:4326');
    let projected = new LonLatCoordinate(lonlat)
      .toProjection(projection);

    let x = (projected.x - metadata.x_offset) / metadata.x_scale;
    let y = (projected.y - metadata.y_offset) / metadata.y_scale;
    return [x, y];
  });


  return newPoints;
};

